import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import SweetAlert from 'sweetalert2';
import { API } from '@/app/api/config';
import { waitForHoldRelease } from '@/modules/common/hold-release';

// axios instance 생성
export const customAxios: AxiosInstance = axios.create({
  baseURL: `${process.env.NEXT_PUBLIC_API_BASE_URL}`,
  withCredentials: true,
  headers: {
    'Content-Type': `application/json`,
    Accept: 'application/json',
  },
});

// 결제 페이지에서 뒤로가기로 막 나온 직후라면, 점유 해제 요청이 서버에서 끝난 뒤에 조회가 나가도록 잠깐 기다린다.
customAxios.interceptors.request.use(async (config) => {
  await waitForHoldRelease();
  return config;
});

let isRefreshing = false;
let refreshWaiters: Array<(ok: boolean) => void> = [];

function notifyRefreshWaiters(ok: boolean) {
  refreshWaiters.forEach((resolve) => resolve(ok));
  refreshWaiters = [];
}

customAxios.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    if (error.response) {
      const { status, data, config: originalRequest } = error.response;

      // 주기적으로 반복 호출되는 요청처럼 호출한 쪽에서 오류를 직접 처리하는 경우, 공통 오류 알림이 쌓이지 않게 건너뛴다.
      const skipErrorAlert = originalRequest?.skipErrorAlert;

      if (status === Number(500)) {
        if (!skipErrorAlert) {
          SweetAlert.fire({
            text: `일시적인 현상이니 잠시 후 다시 시도해 주세요.(오류코드:500)`,
          });
        }

        return Promise.reject(error);
      }

      if (status === Number(401)) {
        const isRefreshCall = originalRequest?.url?.includes(API.AUTHENTICATION);
        const alreadyRetried = originalRequest?._retry;
        const skipAuthRedirect = originalRequest?.skipAuthRedirect;

        // 리프레시 요청 자체의 401은 이 자리에서 알리지 않는다.
        // 이 요청을 기다리고 있는 원본 요청 쪽 catch에서 skipAuthRedirect를 보고 판단한다.
        if (isRefreshCall) {
          return Promise.reject(error);
        }

        if (!alreadyRetried) {
          originalRequest._retry = true;

          if (!isRefreshing) {
            isRefreshing = true;
            try {
              // 재발급 실패(무효한 출입증 403 등)는 서버 문구를 팝업으로 띄우지 않는다. 실패 시 처리는 아래 catch가 맡는다
              // (로그인이 필요한 요청이면 로그인 안내, 로그인 확인용 요청이면 조용히 로그아웃 상태로).
              await customAxios.put(API.AUTHENTICATION, undefined, { skipErrorAlert: true } as AxiosRequestConfig);
              isRefreshing = false;
              notifyRefreshWaiters(true);
              return customAxios(originalRequest);
            } catch (refreshError) {
              isRefreshing = false;
              notifyRefreshWaiters(false);
              if (!skipAuthRedirect) {
                SweetAlert.fire({ title: '로그인 후 이용해주세요. :)' }).then(() => {
                  window.location.href = `/login`;
                });
              }
              return Promise.reject(error);
            }
          }

          const refreshed = await new Promise<boolean>((resolve) => refreshWaiters.push(resolve));
          if (refreshed) {
            return customAxios(originalRequest);
          }
          return Promise.reject(error);
        }

        if (!skipAuthRedirect) {
          SweetAlert.fire({ title: '로그인 후 이용해주세요. :)' }).then(() => {
            window.location.href = `/login`;
          });
        }

        return Promise.reject(error);
      }

      const customStatus = data?.error?.status;
      const message = data?.error?.message;
      if (!skipErrorAlert && customStatus != null && status === customStatus) {
        SweetAlert.fire({ title: message }).then(() => {
          return;
        });
      }
    }

    return Promise.reject(error);
  },
);
